// packages/core/src/progress/ProgressStore.ts
// ─────────────────────────────────────────────────────────────────────────
// PROGRESS STORAGE INTERFACE — the platform seam.
//
// Core owns the lesson-progress MODEL and all derivation (status ladder,
// module rollups). It owns NO storage: every read/write goes through this
// tiny synchronous key-value interface, which each app implements.
//
//   • web   → localStorage impl (apps/web/src/lib/progress/webProgressStore.ts),
//             preserving the historical key prefixes so export + migration
//             keep working unchanged.
//   • native → MMKV / SQLite-backed impl (Expo phase), same interface.
//
// Synchronous by design: lesson progress is small, hot, and read during
// render (module status on every Learn view). An async store would force
// every reader to become async/suspense. localStorage is sync; MMKV is sync;
// SQLite-via-a-sync-KV-cache is sync. Keep this contract sync and let any
// async persistence (e.g. server sync) happen out of band, exactly as the
// existing localStorage + operation-log split already does.
//
// This mirrors the established core/app split: pure logic in core, the
// platform adapter injected (see AudioManifest pure resolution + per-app
// fetch, and the SyncEngine + WebNetworkAdapter pair).
// ─────────────────────────────────────────────────────────────────────────

export interface ProgressStore {
	/** Read a raw string value, or null if absent. Never throws. */
	get(key: string): string | null;
	/** Write a raw string value. Never throws (best-effort persistence). */
	set(key: string, value: string): void;
	/** Remove a key. Never throws. */
	remove(key: string): void;
	/**
	 * All keys beginning with `prefix`. Used for module rollups and export.
	 * Order is not guaranteed. Never throws (returns [] on failure).
	 */
	keys(prefix: string): string[];
}

// ── Key layout ────────────────────────────────────────────────────────────
// Centralized here so core and every app impl agree byte-for-byte. These
// prefixes are HISTORICAL and load-bearing: localExport.ts groups by them and
// migrateLocalStorage.ts reads the legacy boolean. Do not change them without
// a migration.

/** Rich per-lesson record: nis.lessonProgress.{moduleId}.{lessonId} */
export const PROGRESS_PREFIX = 'nis.lessonProgress.';

/** Legacy completion boolean: nis.lesson.{moduleId}.{lessonId} = "true" */
export const LEGACY_BOOLEAN_PREFIX = 'nis.lesson.';

export function progressKey(moduleId: string, lessonId: string): string {
	return `${PROGRESS_PREFIX}${moduleId}.${lessonId}`;
}

export function legacyKey(moduleId: string, lessonId: string): string {
	return `${LEGACY_BOOLEAN_PREFIX}${moduleId}.${lessonId}`;
}
